import React, { useEffect, useState } from "react";
import {
  getFeedbackFormTranslations,
  updateFeedbackFormTranslations,
} from "@/services/commonconfig";

type FeedbackItem = {
  translation_key: string;
  en: string;
  fr: string;
};

const FeedbackFormConfig: React.FC = () => {
  const [formItems, setFormItems] = useState<FeedbackItem[]>([]);
  const [originalItems, setOriginalItems] = useState<FeedbackItem[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    void fetchData();
  }, []);

  const fetchData = async () => {
    const response = await getFeedbackFormTranslations();

    setFormItems(response);
    setOriginalItems(JSON.parse(JSON.stringify(response)));
  };

  const hasChanges =
    JSON.stringify(formItems) !== JSON.stringify(originalItems);

  const handleEnglishChange = (
    rowIndex: number,
    newValue: string
  ) => {
    const updatedRows = [...formItems];

    updatedRows[rowIndex] = {
      ...updatedRows[rowIndex],
      en: newValue,
    };

    setFormItems(updatedRows);
  };

  const handleFrenchChange = (
    rowIndex: number,
    newValue: string
  ) => {
    const updatedRows = [...formItems];

    updatedRows[rowIndex] = {
      ...updatedRows[rowIndex],
      fr: newValue,
    };

    setFormItems(updatedRows);
  };

  const handleCancel = () => {
    setFormItems(JSON.parse(JSON.stringify(originalItems)));
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);

      await updateFeedbackFormTranslations(formItems);

      setOriginalItems(JSON.parse(JSON.stringify(formItems)));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="border border-gray-200 rounded-md bg-[#fbfaf7] p-3 space-y-4">
      <div>
        <p className="text-xs text-gray-600">
          Configure feedback form translations.
        </p>
      </div>

      <div className="grid grid-cols-[400px_1fr_1fr] gap-3 text-xs font-semibold text-gray-600 border-b pb-2">
        <div>Translation Key</div>
        <div>English</div>
        <div>French</div>
      </div>

      {formItems.map((item, index) => (
        <div
          key={item.translation_key}
          className="grid grid-cols-[400px_1fr_1fr] gap-3 items-center text-xs"
        >
          <div className="font-medium text-gray-700 break-all">
            {item.translation_key}
          </div>

          <input
            type="text"
            value={item.en || ""}
            onChange={(e) =>
              handleEnglishChange(index, e.target.value)
            }
            className="border border-gray-300 rounded px-3 py-1 focus:outline-none focus:ring-1 focus:ring-[var(--color-brand)]"
          />

          <input
            type="text"
            value={item.fr || ""}
            onChange={(e) =>
              handleFrenchChange(index, e.target.value)
            }
            className="border border-gray-300 rounded px-3 py-1 focus:outline-none focus:ring-1 focus:ring-[var(--color-brand)]"
          />
        </div>
      ))}

      <div className="flex justify-end gap-2 items-center">
        {hasChanges && (
          <div className="text-orange-600 border border-orange-300 bg-orange-50 px-2 py-1 rounded text-xs">
            Unsaved Changes
          </div>
        )}

        <button
          onClick={handleCancel}
          disabled={!hasChanges || isSaving}
          className="bg-[#e7e5e0] px-3 py-1 rounded text-xs disabled:opacity-50"
        >
          Cancel
        </button>

        <button
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
          className="bg-[var(--color-brand)] text-white px-3 py-1 rounded text-xs disabled:opacity-50"
        >
          {isSaving ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
};

export default FeedbackFormConfig;